import { useCallback, useContext, useEffect, useRef, useState } from "react";
import ToastContext from "../../context/toastProvider";
import Button from "../atoms/button";
import Loading from "../atoms/loading";
import {
    getHealth,
    reapplyConfig,
    getConfig,
    getConfigById,
    saveConfig,
} from "./functions";

export function statusLabel(status) {
    if (status === "aligned") return "Alinhado";
    if (status === "drift") return "Desalinhado";
    return "Desconhecido";
}

/** Resumo em linguagem simples (1–2 parágrafos) + se alguma ação é recomendada. */
export function buildStatusSummary(health) {
    if (!health) {
        return {
            paragraphs: [
                "Ainda não foi possível ler o status do bot. Clique em Atualizar ou aguarde alguns segundos.",
            ],
            actionHint: null,
        };
    }

    const status = health.status ?? "unknown";
    const killOn = health.kill?.enabled !== false;
    const channels = health.channels;
    const open = channels?.open ?? 0;
    const closed = channels?.closed ?? 0;
    const connecting = channels?.connecting ?? 0;
    const mismatch = health.drift?.version_mismatch === true;
    const chunks = Number(health.runtime?.chunk_count || 0);
    const hasRuntime = health.runtime != null;

    const paragraphs = [];
    let actionHint = null;

    if (status === "aligned") {
        paragraphs.push(
            "Tudo certo: a configuração salva neste painel é a mesma que o bot está usando agora. Nenhuma ação obrigatória."
        );
    } else if (status === "drift") {
        if (!hasRuntime || !health.runtime?.version_id) {
            paragraphs.push(
                "O bot parece sem a configuração ativa na memória de execução (Redis). Em geral isso acontece após reinício ou falha de sync."
            );
            actionHint = "Recomendado: Reaplicar — copia a versão salva para o bot voltar a funcionar com o conteúdo atual.";
        } else if (mismatch) {
            paragraphs.push(
                "Desalinhado significa que o que está salvo no painel não é o que o bot está usando agora — as versões divergiram."
            );
            actionHint =
                "Recomendado: Reaplicar (usa a versão ativa do painel no bot). Use Rollback só se quiser voltar a uma versão antiga.";
        } else if (chunks < 1) {
            paragraphs.push(
                "A configuração no bot parece incompleta (falta o conteúdo de conhecimento/chunks). O painel e o bot podem ter o mesmo ID, mas o conteúdo não carregou direito."
            );
            actionHint = "Recomendado: Reaplicar para reescrever o conteúdo completo no bot.";
        } else {
            paragraphs.push(
                "Há divergência entre o que está salvo e o que o bot está usando. Vale conferir e sincronizar."
            );
            actionHint = "Recomendado: Reaplicar. Se o problema continuar, avise ops.";
        }
    } else {
        paragraphs.push(
            "Não foi possível confirmar se o bot está com a configuração certa (leitura incompleta do status). Isso não prova que o bot está errado — só que a checagem falhou."
        );
        actionHint = "Tente Atualizar. Se continuar assim, avise ops — não use Rollback por causa disso.";
    }

    const botParts = [];
    if (!killOn) {
        botParts.push(
            "O interruptor do bot está desligado (Kill OFF): o fluxo automático não responde até religar no card Kill abaixo."
        );
        if (!actionHint) {
            actionHint = "Se o atendimento automático deveria estar ativo, ligue o Kill.";
        }
    } else {
        botParts.push("O bot está ligado (Kill ON).");
    }

    if (open > 0 && closed === 0 && connecting === 0) {
        botParts.push("WhatsApp conectado.");
    } else if (connecting > 0 && open === 0) {
        botParts.push("WhatsApp ainda está conectando — aguarde ou confira a aba Canal.");
        if (!actionHint || status === "aligned") {
            actionHint = "Aguarde a conexão do WhatsApp ou abra a aba Canal se demorar.";
        }
    } else if (closed > 0 && open === 0) {
        botParts.push("WhatsApp desconectado — mensagens não entram até reconectar na aba Canal.");
        if (!actionHint || status === "aligned") {
            actionHint = "Reconecte o WhatsApp na aba Canal (QR). Reaplicar não resolve desconexão.";
        }
    } else if (open > 0 && closed > 0) {
        botParts.push(`WhatsApp parcial: ${open} conectado(s) e ${closed} fechado(s) — confira a aba Canal.`);
    } else {
        botParts.push("Nenhuma instância de WhatsApp reportada — confira a aba Canal.");
        if (!actionHint || status === "aligned") {
            actionHint = "Confira a aba Canal; sem WhatsApp o bot não recebe mensagens.";
        }
    }

    paragraphs.push(botParts.join(" "));

    return { paragraphs, actionHint };
}

const statusColorClass = (status) => {
    if (status === "aligned") return "bg-green-700 text-green-100";
    if (status === "drift") return "bg-red-700 text-red-100";
    return "bg-gray-600 text-gray-200";
};

function formatDate(value) {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("pt-BR");
    } catch {
        return String(value);
    }
}

function shortId(id) {
    if (!id) return "—";
    const s = String(id);
    return s.length > 12 ? `${s.slice(0, 8)}…` : s;
}

function waSummary(channels) {
    if (!channels) return "—";
    const open = channels.open ?? 0;
    const closed = channels.closed ?? 0;
    if (open > 0 && closed === 0) return "aberto";
    if (closed > 0 && open === 0) return "fechado";
    if (open > 0 && closed > 0) return `${open} aberto / ${closed} fechado`;
    if (channels.connecting > 0) return "conectando";
    return "nenhum";
}

const BotOpsHealthPanel = ({ token, onAfterMutation, activeVersion }) => {
    const { goodToast, badToast } = useContext(ToastContext);

    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [versions, setVersions] = useState([]);
    const [rollbackOpen, setRollbackOpen] = useState(false);
    const [rollbackVersionId, setRollbackVersionId] = useState("");
    const [rollbackBusy, setRollbackBusy] = useState(false);

    const badToastRef = useRef(badToast);
    useEffect(() => {
        badToastRef.current = badToast;
    }, [badToast]);

    const fetchHealth = useCallback(async () => {
        const data = await getHealth(token);
        if (data?.type === "error") {
            badToastRef.current(data.message || "Erro ao consultar funcionamento");
            return false;
        }
        setHealth(data);
        return true;
    }, [token]);

    const loadInitial = useCallback(async () => {
        setLoading(true);
        await fetchHealth();
        setLoading(false);
    }, [fetchHealth]);

    useEffect(() => {
        loadInitial();
    }, [loadInitial]);

    useEffect(() => {
        if (activeVersion == null) return;
        fetchHealth();
    }, [activeVersion, fetchHealth]);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchHealth();
        }, 30000);
        return () => clearInterval(interval);
    }, [fetchHealth]);

    const handleRefresh = async () => {
        setBusy(true);
        const ok = await fetchHealth();
        setBusy(false);
        if (ok) goodToast("Funcionamento atualizado");
    };

    const handleReapply = async () => {
        const confirmed = window.confirm(
            "Reaplicar copia a configuração salva neste painel para o bot voltar a usá-la agora (sem criar versão nova). Continuar?"
        );
        if (!confirmed) return;

        setBusy(true);
        const data = await reapplyConfig(token);
        setBusy(false);

        if (data?.type === "error") {
            badToast(data.message || "Erro ao reaplicar configuração");
            return;
        }

        goodToast("Configuração reaplicada no Redis");
        await fetchHealth();
        if (typeof onAfterMutation === "function") {
            await onAfterMutation();
        }
    };

    const openRollbackModal = async () => {
        setBusy(true);
        const configData = await getConfig(token);
        setBusy(false);

        if (configData?.type === "error") {
            badToast(configData.message || "Erro ao carregar versões");
            return;
        }

        const list = Array.isArray(configData.versions) ? configData.versions : [];
        setVersions(list);
        setRollbackVersionId(list[0]?.id ?? "");
        setRollbackOpen(true);
    };

    const closeRollbackModal = () => {
        setRollbackOpen(false);
        setRollbackVersionId("");
        setVersions([]);
    };

    const handleRollbackConfirm = async () => {
        if (!rollbackVersionId) {
            badToast("Selecione uma versão para rollback");
            return;
        }

        const confirmed = window.confirm(
            `Voltar o conteúdo da versão ${rollbackVersionId}? Será criada uma versão nova (histórico preservado) e o bot passará a usá-la.`
        );
        if (!confirmed) return;

        setRollbackBusy(true);
        const versionData = await getConfigById(token, rollbackVersionId);
        if (versionData?.type === "error") {
            setRollbackBusy(false);
            badToast(versionData.message || "Erro ao carregar versão selecionada");
            return;
        }

        const saveData = await saveConfig(token, {
            system_prompt: versionData.system_prompt ?? "",
            guardrails: versionData.guardrails ?? "",
            knowledge: Array.isArray(versionData.knowledge)
                ? versionData.knowledge.map((k) => ({
                      filename: k.filename ?? "",
                      content: k.content ?? "",
                  }))
                : [],
            modelo: versionData.modelo ?? "",
            temperatura: versionData.temperatura ?? null,
        });
        setRollbackBusy(false);

        if (saveData?.type === "error") {
            badToast(saveData.message || "Erro ao restaurar versão");
            return;
        }

        goodToast("Rollback concluído — nova versão criada");
        closeRollbackModal();
        await fetchHealth();
        if (typeof onAfterMutation === "function") {
            await onAfterMutation();
        }
    };

    if (loading) {
        return (
            <section className="rounded-lg border border-[#d59106] p-4 mb-6">
                <Loading classes="m-auto mt-2" />
            </section>
        );
    }

    const status = health?.status ?? "unknown";
    const killOn = health?.kill?.enabled !== false;
    const mongoId = health?.mongo?.active_version;
    const redisId = health?.runtime?.version_id;
    const chunks = health?.runtime?.chunk_count;
    const source = health?.runtime?.source ?? health?.kill?.source ?? "—";
    const summary = buildStatusSummary(health);

    return (
        <>
            <section className="rounded-lg border border-[#d59106] p-4 mb-6">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                    <p className="font-medium text-lg text-[#d59106]">Funcionamento</p>
                    <div className="flex flex-wrap gap-2">
                        <span
                            className={`text-xs px-2 py-1 rounded ${statusColorClass(status)}`}
                            title={
                                status === "drift"
                                    ? "Painel e bot não estão com a mesma configuração"
                                    : status === "aligned"
                                      ? "Painel e bot usam a mesma configuração"
                                      : "Não foi possível confirmar o alinhamento"
                            }
                        >
                            {statusLabel(status)}
                        </span>
                        <span
                            className={`text-xs px-2 py-1 rounded ${
                                killOn ? "bg-green-800 text-green-100" : "bg-red-800 text-red-100"
                            }`}
                            title={
                                killOn
                                    ? "Bot ligado — fluxo automático ativo"
                                    : "Bot desligado — fluxo automático parado"
                            }
                        >
                            Bot {killOn ? "ligado" : "desligado"}
                        </span>
                        <span
                            className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-200"
                            title="Estado do WhatsApp (Evolution)"
                        >
                            WA {waSummary(health?.channels)}
                        </span>
                    </div>
                </div>

                <div
                    className="text-sm text-gray-300 mb-3 space-y-2 rounded-lg bg-gray-900/60 p-3 border border-[#b47c06]/20"
                    data-testid="health-summary"
                >
                    {summary.paragraphs.map((p, i) => (
                        <p key={i}>{p}</p>
                    ))}
                    {summary.actionHint ? (
                        <p className="text-[#d59106] font-medium">{summary.actionHint}</p>
                    ) : null}
                </div>

                <p className="text-xs text-gray-400 font-mono mb-4 break-all">
                    Detalhe técnico: mongo={shortId(mongoId)} · redis={shortId(redisId)} · chunks=
                    {chunks ?? "—"} · source={source}
                </p>

                {health?.checked_at ? (
                    <p className="text-xs text-gray-500 mb-3">
                        Verificado em {formatDate(health.checked_at)}
                    </p>
                ) : null}

                <div className="flex flex-wrap gap-2">
                    <Button
                        click={handleRefresh}
                        disabled={busy || rollbackBusy}
                        classes="bg-gray-600 hover:bg-gray-500 px-3 py-1 font-medium text-white"
                    >
                        Atualizar
                    </Button>
                    <Button
                        click={handleReapply}
                        disabled={busy || rollbackBusy}
                        classes="bg-blue-300 hover:bg-blue-400 px-3 py-1"
                    >
                        Reaplicar
                    </Button>
                    <Button
                        click={openRollbackModal}
                        disabled={busy || rollbackBusy}
                        classes="bg-amber-500 hover:bg-amber-600 px-3 py-1"
                    >
                        Rollback…
                    </Button>
                </div>
            </section>

            {rollbackOpen ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="rollback-title"
                >
                    <div className="bg-gray-900 border border-[#d59106] rounded-lg p-4 max-w-lg w-full max-h-[80vh] overflow-y-auto">
                        <h2 id="rollback-title" className="font-medium text-lg mb-3">
                            Rollback de versão
                        </h2>
                        <p className="text-sm text-gray-300 mb-4">
                            Escolha uma versão anterior. Será criada uma nova versão ativa com o
                            mesmo conteúdo (auditável).
                        </p>

                        {versions.length === 0 ? (
                            <p className="text-sm text-gray-400 mb-4">Nenhuma versão disponível.</p>
                        ) : (
                            <select
                                value={rollbackVersionId}
                                onChange={(e) => setRollbackVersionId(e.target.value)}
                                className="w-full text-black p-2 rounded mb-4"
                            >
                                {versions.map((v) => (
                                    <option key={v.id} value={v.id}>
                                        {v.id} — {formatDate(v.created_at)}
                                        {v.modelo ? ` — ${v.modelo}` : ""}
                                        {v.temperatura != null ? ` @${v.temperatura}` : ""}
                                        {v.preview ? ` — ${v.preview.slice(0, 40)}` : ""}
                                    </option>
                                ))}
                            </select>
                        )}

                        <div className="flex flex-wrap gap-2 justify-end">
                            <Button
                                click={closeRollbackModal}
                                disabled={rollbackBusy}
                                classes="bg-gray-600 hover:bg-gray-500 px-3 py-1 font-medium text-white"
                            >
                                Cancelar
                            </Button>
                            <Button
                                click={handleRollbackConfirm}
                                disabled={rollbackBusy || versions.length === 0}
                                classes="bg-[#d59106] hover:bg-[#b47c06] px-3 py-1"
                            >
                                {rollbackBusy ? "Restaurando…" : "Confirmar rollback"}
                            </Button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default BotOpsHealthPanel;
